// import library
import axios from "axios";
import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import moment from "moment";
// import components
import Layout from "../../layouts/Layout";
import LoaderIcon from "../../components/Common/Icon/Loader";
import Sort from "../../components/Common/Table/Sort";
// import files
import { env } from "../../env";
import VirtualCart from "../../components/Common/CreateOrder/VirtualCart";
import { logEvent } from "../../analytics";

const OrderView = () => {
    // variables
    const navigate = useNavigate();
    const { t } = useTranslation();
    const locale = localStorage.getItem('locale');
    const location = useLocation();
    const orderid = location.pathname.split('/')[3];
    const [isLoader, setIsLoader] = useState(true);
    const [orderData, setOrderData] = useState({});
    const [itemsData, setItemsData] = useState([]);
    const [filterData, setFilteredData] = useState([]);
    const [deliveryDays, setDeliveryDays] = useState(0);
    const [defaultProductImage, setDefaultProductImage] = useState("");
    const [orderStatus, setOrderStatus] = useState([]);
    const [showOrderAgainButton, setShowOrderAgainButton] = useState(true);
    const formatter = new Intl.NumberFormat('de-DE', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });

    const [virtualCartItems, setVirtualCartItems] = useState({
        "itemsCount" : 0,
        "totalQuantity" : 0,
        "netAmount" : 0,
        "vats" : 0,
        "totalAmount" : 0,
        "discount" : 0,
        "transportationFee" : 0,
        "totalPoints" : 0,
        "totalTaxes" : 0
    });

    const [finalCartItems, setFinalCartItems] = useState({
        "itemsCount" : 0,
        "totalQuantity" : 0,
        "netAmount" : 0,
        "vats" : 0,
        "totalAmount" : 0,
        "discount" : 0,
        "transportationFee" : 0,
        "totalPoints" : 0,
        "totalTaxes" : 0
    });

    // functions
    useEffect(() => {
        const baseUrl = env.REACT_APP_API_URL;
        const token = localStorage.getItem('token');

        const headers = {
            "Accept" : "Application/json",
            "Content-Type" : "Application/json",
            "Authorization" : `Bearer ${token}`
        };

        const fetchData = async () => {
            getDefaultProductImage(baseUrl, token);
            await axios.get(baseUrl+"/api/Product/GetCustomerOrderDetail?orderid="+orderid, { headers })
            .then((response) => {
                if(!response.data.success) {
                    if(response.data.orderdate) {
                        const formatDate = response.data.orderdate.split("-");
                        const formattedDate = formatDate[2]+'-'+formatDate[1]+'-'+formatDate[0]
                        response.data.orderdate = moment(formattedDate).format("DD/MM/YYYY")
                    }
                    setOrderData(response.data);
                    
                    if(response.data.details.length > 0) {
                        setItemsData(response.data.details);
                        setFilteredData(response.data.details);

                        setFinalCartItems({
                            ...finalCartItems,
                            "itemsCount" : response.data.noOfItems,
                            "totalQuantity" : response.data.totalqty,
                            "netAmount" : response.data.subTotal,
                            "vats" : response.data.vatAmount,
                            "totalAmount" : response.data.finalAmount,
                            "discount" : 0,
                            "transportationFee" : response.data.totaltransportation,
                            "discountAmount" : response.data.discountAmount,
                            "totalTaxes" : response.data?.total_tax_amount,
                        });
                       
                        setVirtualCartItems({
                            ...virtualCartItems,
                            "itemsCount" : response.data.noOfItems,
                            "totalQuantity" : response.data.totalqty,
                            "netAmount" : response.data.subTotal,
                            "vats" : response.data.vatAmount,
                            "totalAmount" : response.data.finalAmount,
                            "discount" : 0,
                            "transportationFee" : response.data.totaltransportation,
                            "discountAmount" : response.data.discountAmount,
                            "totalTaxes" : response.data?.total_tax_amount,
                        });
                    } else {
                        setShowOrderAgainButton(false);
                    }
                    getCustomerDetail(baseUrl, token, response.data.customerID, response.data.orderSeriesID);
                    clearCartStorage();
                } else {
                    Swal.fire({
                        title: `${ t("warning")}!`,
                        text: `${ t("something went wrong")}!`,
                        icon: "warning",
                        confirmButtonColor: "#3085d6",
                        confirmButtonText: "ok"
                    });
                    navigate('/order/history');
                }
            }).catch((error) => {
                console.log(error);
                if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("userInfo");
                    window.location.reload();
                }
            });
        };
        fetchData();
    }, []);

    const getDefaultProductImage = async (url, token) => {
        const headers = {
            "Accept" : "Application/json",
            "Content-Type" : "Application/json",
            "Authorization" : `Bearer ${token}`
        };

        await axios.get(url+"/api/User/GetDefaultImage",{ headers })
        .then((response) => {
            setDefaultProductImage(response.data.productDefaultImage);
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const getCustomerDetail = async (url, token, id, seriesId) => {
        await axios.post(url+"/api/Customer/GetCustomerDetail", {customerid: id},{ 
            headers: {
                "Content-Type" : "multipart/form-data",
                "Authorization" : `Bearer ${token}`
            }
        }).then((response) => {
            if(response.data.length > 0) {
                setDeliveryDays(response.data[0].noofdeliverydays);
                getOrderStatus(url, token);
            }
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const getOrderStatus = async (url, token) => {
        const headers = {
            "Accept" : "Application/json",
            "Content-Type" : "Application/json",
            "Authorization" : `Bearer ${token}`
        };

        await axios.get(url+`/api/Product/GetOrderDetailsStatuses?Order_id=${orderid}`, { headers })
        .then((response) => {
            setOrderStatus(response.data.data);
            setIsLoader(false);
        }).catch((error) => {
            console.log(error);
        });
    };
    const clearCartStorage = () => {
        localStorage.removeItem('co_tab');
        localStorage.removeItem('co_coupon');
        localStorage.removeItem('co_comment');
        localStorage.removeItem('co_method');
        localStorage.removeItem('co_id');
        localStorage.removeItem('co_quantity');
        localStorage.removeItem('co_qty_message');
        localStorage.removeItem('co_items');
        localStorage.removeItem('co_calculate');
        localStorage.removeItem('co_code');
        localStorage.removeItem('co_calculate_json');
        localStorage.removeItem('co_calculate_button');
        localStorage.removeItem('co_calculate_filter');
    };
    const handleLog = (id) => {
        logEvent('order_history_detail_page', 'order_again', 'redirect_to_create_order_again', id);
    };

    return (
        <Layout>
            <div className="space-y-2 sm:space-y-4">
                {/* first box */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="sm:text-2xl font-semibold capitalize">{ t('order detail') }</h1>
                        <p className="text-light-400 text-xs sm:text-sm capitalize">{ t('orders') } / <NavLink to="/order/history" >{ t('order history') }</NavLink> / { t('order detail') }</p>
                    </div>
                    <div className="hidden sm:flex items-center justify-end">
                        <div>
                            <NavLink to="/order/history" >
                                <button className="text-center w-full px-2 sm:px-4 text-xs font-medium lg:text-base py-2 bg-white text-blue-normal border border-blue-normal rounded-lg tracking-wide capitalize">{ t('close')}</button>
                            </NavLink>
                        </div>
                        <div className="sm:ml-4">
                            {showOrderAgainButton && (
                                <NavLink to={`/order/history/create/${orderid}/${orderData.customerID}`} onClick={handleLog(orderid)}>
                                    <button className="text-center px-2 sm:px-4 text-xs font-medium lg:text-base py-2 text-white bg-blue-normal border border-blue-normal rounded-lg tracking-wide capitalize">{ t('order again')}</button>
                                </NavLink>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex sm:hidden items-center justify-end space-x-4">
                    <div>
                        <NavLink to="/order/history" >
                            <button className="text-center w-full px-2 sm:px-4 text-xs font-medium lg:text-base py-2 bg-white text-blue-normal border border-blue-normal rounded-lg tracking-wide capitalize">{ t('close')}</button>
                        </NavLink>
                    </div>
                    <div className="sm:ml-4">
                        {showOrderAgainButton && (
                            <NavLink to={`/order/history/create/${orderid}/${orderData.customerID}`} onClick={handleLog(orderid)}>
                                <button className="text-center px-2 sm:px-4 text-xs font-medium lg:text-base py-2 text-white bg-blue-normal border border-blue-normal rounded-lg tracking-wide capitalize">{ t('order again')}</button>
                            </NavLink>
                        )}
                    </div>
                </div>
                {
                    isLoader
                    ?
                        <LoaderIcon />
                    :
                    <div className="space-y-2 sm:space-y-4">
                        <div className="p-2 sm:p-4 bg-white border shadow-around border-white rounded-lg grid lg:grid-cols-3 gap-8">
                            <div className="flex items-center sm:items-start lg:flex-col justify-between lg:min-h-28">
                                <div>
                                    <h1 className="font-normal text-sm text-gray-dark capitalize">{ t('order number')}:</h1>
                                    <div className="font-semibold text-xs lg:text-base">#{orderData.orderid}</div>
                                </div>
                                <div className="capitalize font-normal text-xs lg:text-sm bg-blue-light text-light-400 rounded-md py-2 text-center w-fit px-2 xl:px-4">
                                    { t('date')}: {orderData.orderdate}
                                </div>
                            </div>
                            { orderStatus.length > 0 ?
                                <div className="lg:col-span-2 flex items-center -mt-[5rem] sm:-mt-12 px-10 sm:px-24 h-52 sm:h-36 lg:h-auto">
                                    { orderStatus.map((value, index) => (
                                        <div key={'status'+index} className="contents">
                                            <div className="flex items-center relative">
                                                {index === 0 ?
                                                    <div className="rounded-full p-3 border-2 border-blue-normal bg-blue-normal text-center">
                                                        <img src="../../../assets/icons/check.svg" alt="check" />
                                                    </div>
                                                    :
                                                    <div className="rounded-full text-white px-4 py-2 border-2 border-blue-normal bg-blue-normal text-center">
                                                        {index+1}
                                                    </div>
                                                }   
                                                <div className="absolute top-0 -ml-4 sm:-ml-12 text-center mt-16 sm:w-max">
                                                    <h1 className="font-semibold text-xs sm:text-sm">{ locale === 'gr' ? value.statusDescription : value.enstatusDescription }</h1>
                                                    <p className="font-normal text-[10px] sm:text-xs text-light-400">{moment(value.createdOn).format('DD/MM/YYYY h:mm A')}</p>
                                                </div>
                                            </div>
                                            {orderStatus.length !== (index+1) ? <div className="flex-auto border-t-2 border-black-100"></div> : null}
                                        </div>
                                    ))}
                                </div>
                            : null}
                        </div>
                        {/* items ordered list */}
                        <div className="grid lg:grid-cols-7 bg-white p-2 sm:p-4 rounded-lg border border-white space-y-4 shadow-around">
                            <div className="sm:col-span-2 flex flex-col justify-between min-h-20">
                                <div>
                                    <h1 className="font-semibold lg:text-lg">{orderData.customerName}</h1>
                                </div>
                                <div className="font-normal text-xs lg:text-sm text-gray-dark capitalize">
                                    { t('customer code') }: {orderData.customerCode}
                                </div>
                                <div className="flex items-center text-sm">
                                    <div className="flex">
                                        <img src="../../../assets/icons/location.svg" alt="mail" />
                                        <div className="text-gray-dark ml-1">{orderData.customerAddress}</div>
                                    </div>
                                </div>
                            </div>
                            <div className="sm:col-span-5">
                               <VirtualCart finalCartItems={finalCartItems} />
                            </div>
                        </div>
                        {/* virtual cart */}
                        <div className="py-2 sm:py-4 bg-white border border-white rounded-lg shadow-around">
                            <div className="px-2 sm:px-4 lg:flex lg:justify-between items-center">
                                <div>
                                    <h1 className="lg:text-lg font-bold capitalize">{ t('items ordered')}</h1>
                                </div>
                                <div className="mt-2 sm:pt-0 text-light-400 capitalize">
                                    <div className="grid text-[11px] sm:flex sm:divide-x-2 sm:divide-gray-dark sm:divide-solid sm:justify-end sm:items-center grid-cols-2 gap-1"> 
                                        <div className="flex sm:grid lg:flex sm:px-1 text-light-400 items-center">
                                            <h1 className="capitalize font-semibold">{ t('no. of items')}:</h1>
                                            <div className="font-bold ml-1 sm:ml-0 lg:ml-2">{virtualCartItems.itemsCount}</div>
                                        </div>
                                        <div className="flex sm:grid lg:flex sm:px-1 text-light-400 items-center">
                                            <h1 className="capitalize font-semibold">{ t('total quantity')}:</h1>
                                            <div className="font-bold ml-1 sm:ml-0 lg:ml-2">{formatter.format(virtualCartItems.totalQuantity)}</div>
                                        </div>
                                        <div className="flex sm:grid lg:flex sm:px-1 text-light-400 items-center">
                                            <h1 className="capitalize font-semibold">{ t('net amount')}:</h1>
                                            <div className="font-bold ml-1 sm:ml-0 lg:ml-2">{formatter.format(virtualCartItems.netAmount)} €</div>
                                        </div>
                                        <div className="flex sm:grid lg:flex sm:px-1 text-light-400 items-center">
                                            <h1 className="capitalize font-semibold">{ t('extra taxes')}:</h1>
                                            <div className="font-bold ml-1 sm:ml-0 lg:ml-2">{formatter.format(virtualCartItems.totalTaxes)} €</div>
                                        </div>
                                        <div className="flex sm:grid lg:flex sm:px-1 text-light-400 items-center">
                                            <h1 className="capitalize font-semibold">{ t('total')} <span className="uppercase">{ t('vat')}</span>:</h1>
                                            <div className="font-bold ml-1 sm:ml-0 lg:ml-2">{formatter.format(virtualCartItems.vats)} €</div>
                                        </div>
                                        <div className="flex sm:grid lg:flex sm:px-1 text-light-400 items-center">
                                            <h1 className="capitalize font-semibold">{ t('total amount')}:</h1>
                                            <div className="font-bold ml-1 sm:ml-0 lg:ml-2">{formatter.format(virtualCartItems.totalAmount)} €</div>
                                        </div>
                                    </div> 
                                </div>
                            </div>
                            {/* horizontal line */}
                            <div className="my-3 border-t border-background-normal"></div>
                            {/* items table*/}
                            <div className="relative overflow-x-auto sm:block hidden">
                                <table className="w-full text-xs">
                                    <thead className="text-gray-normal">
                                        <tr>
                                            <th scope="col" className="font-normal p-3 text-left uppercase">
                                                { t('product name')}
                                            </th>
                                            <th scope="col" className="font-normal p-3 text-center uppercase">
                                                { t('quantity')}
                                            </th>
                                            <th scope="col" className="font-normal p-3 text-center uppercase">
                                                { t('initial price')}
                                            </th>
                                            <th scope="col" className="font-normal p-3 text-center uppercase">
                                                { t('net amount')}
                                            </th>
                                            <th scope="col" className="font-normal p-3 text-center uppercase">
                                                { t('extra taxes')}
                                            </th>
                                            <th scope="col" className="font-normal p-3 text-center uppercase">
                                                { t('vat')}
                                            </th>
                                            <th scope="col" className="font-normal p-3 text-center uppercase">
                                                { t('amount')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filterData.map((virtual, key) => (
                                            <tr key={'virtual'+key} className={(filterData.length !== key+1 ? 'border-b border-dashed' : '') +" bg-white hover:bg-gray-50"}>
                                                <td className="p-3 font-semibold">
                                                    <div className="flex">
                                                        <div className="w-14 xl:w-auto">
                                                            <img className="w-8 h-8" src={virtual.image ? virtual.image : defaultProductImage} alt="product-img" />
                                                        </div>
                                                        <div className="ml-2 grid justify-between">
                                                            <h1>{virtual.productname}</h1>
                                                            <p className="text-light-400 text-[10px] capitalize">{ t('product code')}: {virtual.product_code}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-3 text-center text-light-400">
                                                    {formatter.format(virtual.qty)}
                                                </td>
                                                <td className="p-3 text-center text-light-400">
                                                    {formatter.format(virtual.initprice)} €
                                                </td>
                                                <td className="p-3 text-center text-light-400">
                                                    {formatter.format(virtual.subtotal)} €
                                                </td>
                                                <td className="p-3 text-center text-light-400">
                                                    {formatter.format(virtual.extra_taxes)} €
                                                </td>
                                                <td className="p-3 text-center text-light-400">
                                                    {formatter.format(virtual.vatamount)} €
                                                </td>
                                                <td className="p-3 text-center text-light-400">
                                                    {formatter.format(virtual.finalamount)} €
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="sm:hidden block bg-white border border-white rounded-lg space-y-3 p-2 sm:p-4">
                                {(filterData.length > 0) ?
                                    <div className="space-y-3">
                                        { filterData.map((virtual, key) => (
                                            <div key={'virtual'+key}>
                                                <div className="grid space-y-2">
                                                    <div className="flex">
                                                        <img className="border border-black-100 bg-black-100 rounded-lg flex flex-col justify-center items-center w-16 h-16" src={virtual.image ? virtual.image : defaultProductImage} alt="product" />
                                                        <div className="ml-2 flex flex-col justify-between w-full space-y-1">
                                                            <div>
                                                                <div className="font-medium text-xs">{virtual.productname}</div>
                                                                <div className="text-light-400 text-[10px] capitalize">{ t('product code')}: {virtual.product_code}</div>
                                                            </div>
                                                            <div className="flex text-xs justify-between">
                                                                <div className="flex sm:px-3 text-light-400 items-center">
                                                                    <h1 className="capitalize font-medium">{ t('quantity')}:</h1>
                                                                    <div className="font-bold ml-1 sm:ml-2">{formatter.format(virtual.qty)}</div>
                                                                </div>
                                                                <div className="flex sm:px-3 text-light-400 items-center">
                                                                    <h1 className="capitalize font-medium">{ t('initial price')}:</h1>
                                                                    <div className="font-bold ml-1 sm:ml-2">{formatter.format(virtual.initprice)} €</div>
                                                                </div>
                                                            </div>
                                                            <div className="flex text-xs justify-between">
                                                                <div className="flex sm:px-3 text-light-400 items-center">
                                                                    <h1 className="capitalize font-medium">{ t('net amount')}:</h1>
                                                                    <div className="font-bold ml-1 sm:ml-2">{formatter.format(virtual.subtotal)} €</div>
                                                                </div>
                                                                <div className="flex sm:px-3 text-light-400 items-center justify-end">
                                                                    <h1 className="uppercase font-medium">{ t('vat')} :</h1>
                                                                    <div className="font-bold ml-1 sm:ml-2">{formatter.format(virtual.vatamount)} €</div>
                                                                </div>
                                                            </div>
                                                            <div className="flex text-xs justify-between">
                                                                <div className="flex sm:px-3 text-light-400 items-center">
                                                                    <h1 className="capitalize font-medium">{ t('extra taxes')}:</h1>
                                                                    <div className="font-bold ml-1 sm:ml-2">{formatter.format(virtual.extra_taxes)} €</div>
                                                                </div>
                                                            </div>
                                                            <div className="text-right text-sm">
                                                                <span className="capitalize font-medium">{ t('total amount')} : </span>
                                                                <span className="font-bold">
                                                                    {formatter.format(virtual.finalamount)} €
                                                                </span>
                                                            </div> 
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="my-3 border-t-2 border-background-normal"></div>
                                            </div>
                                        ))}
                                    </div>
                                    : 
                                    <div className="py-4 text-center font-bold text-xs sm:text-xl capitalize">
                                        { t('no records available')}
                                    </div>
                                }
                            </div>
                        </div>
                        {/* summary */}
                        <div className="grid sm:grid-cols-2 gap-2 sm:space-x-2">
                            <div className="p-2 sm:p-4 bg-white border border-white shadow-around rounded-lg text-xs sm:text-base h-fit">
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    <div className="flex items-start space-x-1">
                                        <img className="w-5 h-5 mt-0.5" src="../../assets/icons/payment.svg" alt="payment" />
                                        <h1 className="text-light-400 font-semibold capitalize text-xs lg:text-sm">{ t('payment method')}</h1>
                                    </div>
                                    <div className="sm:col-span-2 font-semibold text-sm">{orderData.paymentType ? orderData.paymentType : '--'}</div>
                                </div>
                                <div className="my-3 border-t border-dashed border-gray-dark1"></div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    <div className="flex items-start space-x-1">
                                        <img className="w-5 h-5 mt-0.5" src="../../assets/icons/phone.svg" alt="phone" />
                                        <h1 className="text-light-400 font-semibold capitalize text-xs lg:text-sm">{ t('phone number')}</h1>
                                    </div>
                                    <div className="sm:col-span-2 font-semibold text-sm">
                                        <div className="sm:col-span-2 font-semibold text-sm">{orderData.customerMobile ? orderData.customerMobile : '--'}</div>
                                    </div>
                                </div>
                                <div className="my-3 border-t border-dashed border-gray-dark1"></div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    <div className="flex items-start space-x-1">
                                        <img className="w-5 h-5 mt-0.5" src="../../assets/icons/email.svg" alt="email" />
                                        <h1 className="text-light-400 font-semibold capitalize text-xs lg:text-sm">{ t('email')}</h1>
                                    </div>
                                    <div className="sm:col-span-2 font-semibold text-sm">
                                        <div className="sm:col-span-2 font-semibold text-sm">{orderData.customerEmail ? orderData.customerEmail : '--'}</div>
                                    </div>
                                </div>
                                <div className="my-3 border-t border-dashed border-gray-dark1"></div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    <div className="flex items-start space-x-1">
                                        <img className="w-5 h-5 mt-0.5" src="../../assets/icons/comment.svg" alt="comment" />
                                        <h1 className="text-light-400 font-semibold capitalize text-xs lg:text-sm">{ t('comments')}</h1>
                                    </div>
                                    <div className="sm:col-span-2 font-semibold text-sm">{orderData.comment ? orderData.comment : '--'}</div>
                                </div>
                            </div>
                            <div className="py-2 sm:py-4 bg-white border border-white rounded-lg shadow-around">
                                <div className="px-2 sm:px-4">
                                    <h1 className="sm:text-sm lg:text-lg font-semibold capitalize">{ t('final order summary')}</h1>
                                </div>
                                <div className="my-3 border-t border-background-normal"></div>
                                <div className="px-2 sm:px-4 text-right space-y-4">
                                    <div className="flex justify-between items-center">
                                        <div className="text-light-400 text-sm font-normal capitalize">{ t('no. of items')}</div>
                                        <div className="font-bold text-sm">{finalCartItems.itemsCount}</div>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <div className="text-light-400 text-sm font-normal capitalize">{ t('total quantity')}</div>
                                        <div className="font-bold text-sm">{formatter.format(finalCartItems.totalQuantity)}</div>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <div className="text-light-400 text-sm font-normal capitalize">{ t('discount value')}</div>
                                        <div className="font-bold text-sm">{formatter.format(finalCartItems.discountAmount)}</div>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <div className="text-light-400 text-sm font-normal capitalize">{ t('total net value')}</div>
                                        <div className="font-bold text-sm">{formatter.format(finalCartItems.netAmount)} €</div>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <div className="text-light-400 text-sm font-normal capitalize">{ t('extra taxes')}</div>
                                        <div className="font-bold text-sm">{formatter.format(finalCartItems.totalTaxes)} €</div>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <div className="text-light-400 text-sm font-normal capitalize">{ t('total')} <span className="uppercase">{ t('vat')}</span></div>
                                        <div className="font-bold text-sm">{formatter.format(finalCartItems.vats)} €</div>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <div className="text-light-400 text-sm font-normal capitalize">{ t('total amount')}</div>
                                        <div className="font-bold text-sm">{formatter.format(finalCartItems.totalAmount)} €</div>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <div className="text-light-400 text-sm font-normal capitalize">{ t('transportation fee')}</div>
                                        <div className="font-bold text-sm">{formatter.format(finalCartItems.transportationFee)} €</div>
                                    </div>
                                    <div className="my-3 border-t border-background-normal"></div>
                                    <div className="flex justify-between items-center">
                                        <div className="font-semibold first-letter:capitalize">{ t('amount to be paid')}</div>
                                        <div className="font-bold text-sm">{formatter.format(finalCartItems.totalAmount)} €</div>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <div className="text-light-400 text-xs font-normal capitalize">{ t('coupon code')}:</div>
                                        <div className="font-bold text-xs">--</div>
                                    </div>
                                    {showOrderAgainButton && (
                                        <div>
                                            <NavLink to={`/order/history/create/${orderid}/${orderData.customerID}`} onClick={handleLog(orderid)}>
                                                <button className="w-full text-center px-2 lg:px-4 text-sm font-medium lg:text-base py-2 text-white bg-blue-normal border border-blue-normal rounded-lg tracking-wide capitalize">{ t('order again')}</button>
                                            </NavLink>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                }
            </div>
        </Layout>
    )
}

export default OrderView